// assets/product-custom-property.js
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} textInput - The text input.
 * @property {HTMLElement} characterCount - The character count element.
 */

/**
 * A custom element that manages product custom properties
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  #controller = new AbortController();

  #previewSyncTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.#sectionRoot().addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, {
      signal: this.#controller.signal,
    });
    requestAnimationFrame(() => this.#syncPreviewText());
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    window.clearTimeout(this.#previewSyncTimeout);
  }

  handleInput() {
    this.#updateCharacterCount();
    this.#syncPersonalizationVariant();
    this.#syncPreviewText();
    this.#queuePreviewSync();
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }

  #syncPersonalizationVariant() {
    const { textInput } = this.refs;
    const productId = this.#productId();

    if (!productId || !textInput) return;

    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantPicker) return;

    const targetLabel = textInput.value.trim() ? 'personalized' : 'standard';
    const radio = Array.from(variantPicker.querySelectorAll('input[type="radio"]')).find((input) => {
      if (!(input instanceof HTMLInputElement)) return false;

      return input.name.toLowerCase().includes('personalization') && input.value.toLowerCase().includes(targetLabel);
    });

    if (radio instanceof HTMLInputElement && !radio.checked) {
      radio.click();
      return;
    }

    const select = Array.from(variantPicker.querySelectorAll('select')).find((selectElement) => {
      if (!(selectElement instanceof HTMLSelectElement)) return false;

      return selectElement.name.toLowerCase().includes('personalization');
    });

    if (!(select instanceof HTMLSelectElement)) return;

    const option = Array.from(select.options).find((selectOption) =>
      selectOption.value.toLowerCase().includes(targetLabel)
    );

    if (option && select.value !== option.value) {
      select.value = option.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #handleVariantUpdate = (event) => {
    const productId = this.#productId();
    const updatedProductId = event.detail?.data?.productId;

    if (productId && updatedProductId && updatedProductId !== productId) return;

    this.#queuePreviewSync();
  };

  #queuePreviewSync() {
    window.clearTimeout(this.#previewSyncTimeout);
    this.#previewSyncTimeout = window.setTimeout(() => this.#syncPreviewText(), 100);
  }

  #syncPreviewText() {
    const { textInput } = this.refs;
    if (!textInput) return;

    const personalizationText = textInput.value.trim();
    const previewTargets = this.#previewTargets();
    const activeTargets = new Set(previewTargets);

    for (const preview of this.#sectionRoot().querySelectorAll('.product-personalization-preview')) {
      if (!activeTargets.has(preview.parentElement)) preview.remove();
    }

    if (!personalizationText) {
      for (const preview of this.#sectionRoot().querySelectorAll('.product-personalization-preview')) preview.remove();
      return;
    }

    for (const target of previewTargets) {
      target.classList.add('product-personalization-preview-target');

      let preview = target.querySelector(':scope > .product-personalization-preview');
      if (!(preview instanceof HTMLElement)) {
        preview = document.createElement('div');
        preview.className = 'product-personalization-preview';
        preview.setAttribute('aria-hidden', 'true');
        target.append(preview);
      }

      preview.textContent = personalizationText;
    }
  }

  #previewTargets() {
    const mediaGallery = this.#sectionRoot().querySelector('.product-information__media media-gallery, media-gallery');
    if (!mediaGallery) return [];

    return Array.from(
      mediaGallery.querySelectorAll('slideshow-slide .product-media, .media-gallery__grid > li .product-media')
    ).filter((target) => target instanceof HTMLElement && !target.closest('zoom-dialog') && target.querySelector('img'));
  }

  #sectionRoot() {
    return this.closest('.shopify-section') || document;
  }

  #productId() {
    return this.querySelector('[data-product-id]')?.dataset.productId;
  }
}

customElements.define('product-custom-property-component', ProductCustomProperty);
